import React, { useRef } from 'react';
import {
  Brain, Image as ImageIcon, FileText, Newspaper, TrendingUp,
  Gamepad2, Smartphone, ShieldCheck, CreditCard, ArrowUpRight,
} from 'lucide-react';
import { services } from '../data/mock';
import SceneHeader from './SceneHeader';

const iconMap = {
  Brain, ImageIcon, FileText, Newspaper, TrendingUp,
  Gamepad2, Smartphone, ShieldCheck, CreditCard,
};

const SceneCard = ({ service, index }) => {
  const ref = useRef(null);
  const Icon = iconMap[service.icon] || Brain;

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    el.style.setProperty('--mx', `${x}px`);
    el.style.setProperty('--my', `${y}px`);
    const rx = ((y / r.height) - 0.5) * -4;
    const ry = ((x / r.width)  - 0.5) *  4;
    const inner = el.querySelector('[data-tilt]');
    if (inner) inner.style.transform = `perspective(1200px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    const inner = el.querySelector('[data-tilt]');
    if (inner) inner.style.transform = 'perspective(1200px) rotateX(0) rotateY(0)';
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="spotlight group relative overflow-hidden rounded-2xl reflective transition-all duration-700 hover:border-cyan-400/30"
      data-cursor="hover"
    >
      <div data-tilt className="relative h-full p-7 md:p-8 transition-transform duration-300 ease-out" style={{ transformStyle: 'preserve-3d' }}>
        <div className="flex items-start justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
            Capability / {String(index + 1).padStart(2, '0')}
          </div>
          <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-cyan-300 group-hover:rotate-45 transition-all duration-500" />
        </div>

        <div className="mt-8 flex items-center gap-4">
          <div className="relative w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
            <Icon className="w-5 h-5 text-cyan-300" strokeWidth={1.6} />
            <span className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 via-cyan-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
        </div>

        <h3 className="mt-6 font-light text-[26px] md:text-[32px] leading-[1.05] tracking-[-0.02em] chromatic-text">
          {service.title}
        </h3>
        <p className="mt-3 text-sm text-white/50 leading-relaxed max-w-sm">
          {service.description}
        </p>

        <div className="mt-8 pt-5 border-t border-white/5 flex flex-wrap gap-x-4 gap-y-1">
          {service.features.map((f) => (
            <span key={f} className="text-[11px] uppercase tracking-[0.18em] text-white/40">
              {f}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const ref = useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in-view'); });
    }, { threshold: 0.08 });
    el.querySelectorAll('.reveal, .reveal-stagger').forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="relative py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SceneHeader
          chapter="02 / 06"
          subtitle="Capabilities"
          title={<>Nine disciplines, <span className="font-serif neon-text">one</span> atelier.</>}
          description="Each craft below is led by a specialist team. Together, they build invisible infrastructure for the next decade of digital experience."
        />

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 reveal-stagger">
          {services.map((s, i) => (
            <SceneCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
